import type {
  BossDef,
  EnemyDef,
  EraDef,
  ItemDef,
  MetaUpgradeDef,
  PatternDef,
  UnlockDef,
  UpgradeDef,
} from './types';

/**
 * ContentRegistry: the single lookup point for all data. Built once at boot
 * from the era files + global pools. Enemies/patterns/bosses are namespaced by
 * their era but looked up globally by id, so ids must be unique (convention:
 * prefix with era short-code, e.g. `p1_microbe`, `c2_trilobite`).
 */
export class ContentRegistry {
  readonly eras: EraDef[] = [];
  readonly enemies = new Map<string, EnemyDef>();
  readonly enemyIndex = new Map<string, number>(); // id -> dense index (used by SoA arrays)
  readonly enemyList: EnemyDef[] = [];
  readonly patterns = new Map<string, PatternDef>();
  readonly patternIndex = new Map<string, number>();
  readonly patternList: PatternDef[] = [];
  readonly bosses = new Map<string, BossDef>();
  readonly upgrades = new Map<string, UpgradeDef>();
  readonly upgradeList: UpgradeDef[] = [];
  readonly items = new Map<string, ItemDef>();
  readonly itemList: ItemDef[] = [];
  readonly metaUpgrades: MetaUpgradeDef[] = [];
  readonly unlocks: UnlockDef[] = [];
  readonly errors: string[] = [];

  addEra(era: EraDef): void {
    this.eras.push(era);
    this.eras.sort((a, b) => a.index - b.index);
    for (const e of era.enemies) this.addEnemy(e);
    for (const p of era.patterns) this.addPattern(p);
    this.bosses.set(era.boss.id, era.boss);
  }

  addEnemy(e: EnemyDef): void {
    if (this.enemies.has(e.id)) this.errors.push(`duplicate enemy id ${e.id}`);
    this.enemies.set(e.id, e);
    this.enemyIndex.set(e.id, this.enemyList.length);
    this.enemyList.push(e);
  }

  addPattern(p: PatternDef): void {
    if (this.patterns.has(p.id)) this.errors.push(`duplicate pattern id ${p.id}`);
    this.patterns.set(p.id, p);
    this.patternIndex.set(p.id, this.patternList.length);
    this.patternList.push(p);
  }

  addUpgrades(list: UpgradeDef[]): void {
    for (const u of list) {
      if (this.upgrades.has(u.id)) this.errors.push(`duplicate upgrade id ${u.id}`);
      this.upgrades.set(u.id, u);
      this.upgradeList.push(u);
    }
  }

  addItems(list: ItemDef[]): void {
    for (const i of list) {
      if (this.items.has(i.id)) this.errors.push(`duplicate item id ${i.id}`);
      this.items.set(i.id, i);
      this.itemList.push(i);
    }
  }

  addMetaUpgrades(list: MetaUpgradeDef[]): void {
    this.metaUpgrades.push(...list);
  }

  addUnlocks(list: UnlockDef[]): void {
    this.unlocks.push(...list);
  }

  /** Cross-reference check. Call once after all content is registered. */
  validate(): string[] {
    const errs = [...this.errors];
    const checkPattern = (id: string, where: string) => {
      if (!this.patterns.has(id)) errs.push(`${where}: unknown pattern '${id}'`);
    };
    const checkEnemy = (id: string, where: string) => {
      if (!this.enemies.has(id)) errs.push(`${where}: unknown enemy '${id}'`);
    };
    for (const [id, e] of this.enemies) {
      e.patterns?.forEach((p) => checkPattern(p, `enemy ${id}`));
      if (e.onDeath?.spawn) checkEnemy(e.onDeath.spawn.enemy, `enemy ${id}.onDeath`);
      if (e.onDeath?.pattern) checkPattern(e.onDeath.pattern, `enemy ${id}.onDeath`);
      if (e.behavior.type === 'leap' && e.behavior.landPattern) checkPattern(e.behavior.landPattern, `enemy ${id}.behavior.landPattern`);
      if (e.behavior.type === 'flyover') checkPattern(e.behavior.bombPattern, `enemy ${id}.behavior.bombPattern`);
      if (e.behavior.type === 'assemble') checkEnemy(e.behavior.enemy, `enemy ${id}.behavior.enemy`);
      if (e.behavior.type === 'lockon') checkPattern(e.behavior.lockPattern, `enemy ${id}.behavior.lockPattern`);
    }
    for (const [id, p] of this.patterns) {
      for (const em of p.emitters) {
        if (em.bullet.onExpire) checkPattern(em.bullet.onExpire, `pattern ${id}.onExpire`);
        if (em.bursts === 0 && p.duration === undefined) errs.push(`pattern ${id}: infinite emitter needs duration`);
      }
    }
    for (const [id, b] of this.bosses) {
      for (const ph of b.phases) {
        ph.patterns.forEach((p) => checkPattern(p, `boss ${id}`));
        if (ph.summons) checkEnemy(ph.summons.enemy, `boss ${id}.summons`);
        if (ph.onEnter) checkPattern(ph.onEnter, `boss ${id}.onEnter`);
        if (ph.special?.type === 'shield') checkEnemy(ph.special.enemy, `boss ${id}.special.shield`);
        if (ph.special?.type === 'flight') {
          checkPattern(ph.special.bombPattern, `boss ${id}.special.flight.bombPattern`);
          checkPattern(ph.special.landPattern, `boss ${id}.special.flight.landPattern`);
        }
        if (ph.special?.type === 'subsystems') {
          checkEnemy(ph.special.enemy, `boss ${id}.special.subsystems.enemy`);
          checkPattern(ph.special.podPattern, `boss ${id}.special.subsystems.podPattern`);
          checkPattern(ph.special.exposedPattern, `boss ${id}.special.subsystems.exposedPattern`);
        }
        if (ph.special?.type === 'devour') {
          checkPattern(ph.special.recallPattern, `boss ${id}.special.devour.recallPattern`);
          checkEnemy(ph.special.echoEnemy, `boss ${id}.special.devour.echoEnemy`);
        }
        if (ph.behavior.type === 'leap' && ph.behavior.landPattern) checkPattern(ph.behavior.landPattern, `boss ${id}.behavior.landPattern`);
      }
    }
    for (const era of this.eras) {
      if (era.stages.length !== 3) errs.push(`era ${era.id}: needs exactly 3 stages`);
      era.stages.forEach((s, i) => {
        const where = `era ${era.id} stage ${i}`;
        const positive = (value: number | undefined) => value !== undefined && Number.isFinite(value) && value > 0;
        if (!positive(s.duration)) errs.push(`${where}: duration must be positive and finite`);
        if (!Number.isFinite(s.density.start) || s.density.start < 0 || !Number.isFinite(s.density.end) || s.density.end < 0) errs.push(`${where}: density values must be finite and non-negative`);
        s.pool.forEach((p) => {
          checkEnemy(p.enemy, `${where} pool`);
          if (!positive(p.weight)) errs.push(`${where} pool ${p.enemy}: weight must be positive and finite`);
          if (p.minTime !== undefined && (!Number.isFinite(p.minTime) || p.minTime < 0)) errs.push(`${where} pool ${p.enemy}: minTime must be finite and non-negative`);
        });
        s.waves.forEach((w, waveIndex) => {
          checkEnemy(w.enemy, `${where} wave`);
          if (!Number.isFinite(w.t) || w.t < 0) errs.push(`${where} wave ${waveIndex}: time must be finite and non-negative`);
          if (!positive(w.count)) errs.push(`${where} wave ${waveIndex}: count must be positive and finite`);
          if (waveIndex > 0 && w.t < s.waves[waveIndex - 1].t) errs.push(`${where}: scripted waves must be ordered by time`);
        });
        const hasSustainedSpawns = s.density.end > 0 && s.pool.length > 0;
        if (s.objective?.type === 'survive' && !positive(s.objective.seconds ?? s.duration)) errs.push(`${where}: survive objective needs positive finite seconds`);
        if (s.objective?.type === 'kill' && !positive(s.objective.count)) errs.push(`${where}: kill objective needs a positive finite count`);
        if ((s.objective?.type === 'kill' || s.objective?.type === 'collect') && !hasSustainedSpawns) errs.push(`${where}: ${s.objective.type} objective needs a non-empty pool and positive ending density`);
        if (s.objective?.type === 'waves' && s.waves.length === 0) errs.push(`era ${era.id} stage ${i}: waves objective needs scripted waves`);
        if (s.objective?.type === 'hold' && !positive(s.objective.seconds)) errs.push(`${where}: hold objective needs positive finite seconds`);
        if ((s.objective?.type === 'hold' || s.objective?.type === 'defend' || s.objective?.type === 'escort') && s.objective.radius !== undefined && !positive(s.objective.radius)) errs.push(`${where}: ${s.objective.type} objective radius must be positive and finite`);
        if ((s.objective?.type === 'collect' || s.objective?.type === 'hunt') && !positive(s.objective.count)) errs.push(`${where}: ${s.objective.type} objective needs a positive finite count`);
        if (s.objective?.type === 'hunt') {
          const target = s.objective.enemy;
          if (!this.enemies.has(target)) errs.push(`${where}: hunt objective references missing enemy ${target}`);
          const targetIsReachable = s.pool.some((p) => p.enemy === target) || s.waves.some((w) => w.enemy === target);
          if (!targetIsReachable) errs.push(`${where}: hunt target ${target} is not present in its pool or scripted waves`);
          if (!hasSustainedSpawns && s.waves.filter((w) => w.enemy === target).reduce((total, w) => total + w.count, 0) < s.objective.count) errs.push(`${where}: hunt objective cannot spawn enough ${target}`);
        }
        if (s.objective?.type === 'defend' && !positive(s.objective.seconds)) errs.push(`${where}: defend objective needs positive finite seconds`);
        if ((s.objective?.type === 'defend' || s.objective?.type === 'escort') && s.objective.health !== undefined && !positive(s.objective.health)) errs.push(`${where}: ${s.objective.type} objective health must be positive and finite`);
        if (s.objective?.type === 'escort' && !positive(s.objective.distance)) errs.push(`${where}: escort objective needs positive finite distance`);
        if (s.objective?.type === 'escort' && s.objective.speed !== undefined && !positive(s.objective.speed)) errs.push(`${where}: escort objective speed must be positive and finite`);
        if (s.boss && !this.bosses.has(s.boss.id)) errs.push(`era ${era.id} stage ${i}: unknown boss ${s.boss.id}`);
      });
      if (!era.stages[2].boss) errs.push(`era ${era.id}: stage 3 must be a boss stage`);
      era.loot.forEach((l) => {
        if (!this.items.has(l.itemId)) errs.push(`era ${era.id} loot: unknown item ${l.itemId}`);
      });
    }
    for (const u of this.upgradeList) {
      u.requires?.forEach((r) => {
        if (!this.upgrades.has(r)) errs.push(`upgrade ${u.id} requires unknown '${r}'`);
      });
    }
    return errs;
  }
}
